import AwsS3 from "@uppy/aws-s3";
import Compressor from "@uppy/compressor";
import { Uppy, UppyFile } from "@uppy/core";
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import ImageEditor from "@uppy/image-editor";
import "@uppy/image-editor/dist/style.css";
import { DragDrop, StatusBar, useUppy } from "@uppy/react";
import { useRef } from "react";
// import { useUploaderContext } from "src/contexts/UploaderContext";
import { Css } from "src/Css";
import { AssetResizing, useCreateAssetUploadUrlMutation } from "src/generated/graphql-types";
import "./UppyUploader.css";

export interface UseUppyUploaderProps {
  allowedFileTypes?: Array<string>;
  /** This will emit once per uploaded file */
  onFinish: (file: UppyFile) => void;
  onError?: (file: UppyFile) => void;
  /** This will emit once when all files were uploaded */
  onComplete?: () => void;
  maxNumberOfFiles?: number;
  /** passed to Uppy, start uploading as soon as files are selected? */
  autoProceed?: boolean;
  /** request thumbnail previews for uploaded files */
  previewResizing?: AssetResizing;
}

export function useUppyUploader(props: UseUppyUploaderProps) {
  const { allowedFileTypes, onFinish, onComplete, onError, previewResizing, maxNumberOfFiles = null } = props;

  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // const { setFiles, clearFiles } = useUploaderContext();
  const [createAssetUploadUrl] = useCreateAssetUploadUrlMutation();

  return useUppy(() => {
    const uppy = new Uppy({
      autoProceed: props.autoProceed ?? true,
      restrictions: {
        maxNumberOfFiles,
        minNumberOfFiles: 1,
        allowedFileTypes,
      },
    })
      .use(Compressor, { quality: 0.9 }) // This ensures correct rotation
      .use(ImageEditor, {
        quality: 1,
        cropperOptions: {
          croppedCanvasOptions: {},
          viewMode: 1,
          background: false,
          autoCropArea: 1,
          responsive: true,
        },
        actions: {
          granularRotate: true,
          revert: true,
          rotate: true,
          flip: false,
          zoomIn: false,
          zoomOut: false,
          cropSquare: true,
          cropWidescreen: true,
          cropWidescreenVertical: false,
        },
      })
      .use(AwsS3, {
        getUploadParameters: async (file: UppyFile) => {
          const { data } = await createAssetUploadUrl({
            variables: { input: { contentType: file.type!, previewResizing: previewResizing || null } },
          });

          const { s3Key, uploadUrl: signedUrl, downloadUrl, previewUrl } = data?.createAssetUploadUrl!;
          file.meta = { ...file.meta, s3Key, signedUrl, downloadUrl, previewUrl };

          return {
            method: "PUT",
            url: signedUrl,
            fields: {},
            headers: { "Content-Type": file.type! },
          };
        },
      })
      .on("upload-progress", (file, progress) => {
        const progressPercentage = Math.round((progress.bytesUploaded * 100) / progress.bytesTotal);
        // setFiles({ [file.name]: { name: file.name, progress: progressPercentage } });
      })
      .on("upload-success", (file: UppyFile) => {
        onFinishRef.current?.(file);
      })
      .on("upload-error", (file, error) => {
        console.error(`error with file: ${file.name}, error message: ${error}`);
        onErrorRef.current?.(file);
      });
    uppy.on("complete", () => {
      onCompleteRef.current?.();
      // Once all files completed uploading (and total progress is 100 that means there is not pending retries) call reset
      // to return things to the way they were before any user input.
      // This to clear file list and allow user to upload same file multiple times
      if (uppy.getState().totalProgress === 100) {
        // Clear and close uploader after 5 secs of completing
        // setTimeout(() => clearFiles(), 5000);
        uppy.reset();
      }
    });
    return uppy;
  });
}

export interface UppyUploaderProps extends Omit<UseUppyUploaderProps, "autoProceed"> {
  /** Text that will show in the FileInput **/
  fileInputText?: string;
  /** Text that will show in the drop zone */
  dragDropText?: string;
  dragDropWidth?: number | string;
  dragDropHeight?: number | string;
}

export function UppyUploader(props: UppyUploaderProps) {
  const {
    dragDropText = "Drag & drop files",
    fileInputText = "Choose Files",
    dragDropWidth = 250,
    dragDropHeight = 40,
  } = props;
  const uppy = useUppyUploader({ ...props, autoProceed: true });

  const isUploading = Object.keys(uppy.getState().currentUploads).length > 0;

  const parentWidth = typeof dragDropWidth === "number" ? `${dragDropWidth + 2}px` : `calc(${dragDropWidth} + 2px)`;
  const parentHeight = typeof dragDropHeight === "number" ? `${dragDropHeight + 2}px` : `calc(${dragDropHeight} + 2px)`;

  return (
    <div
      css={
        Css.ba.bcBlue700.bgBlue50.bsDashed
          .borderRadius("8px")
          .if(!isUploading)
          .add("width", parentWidth)
          .add("height", parentHeight).$
      }
    >
      <div
        className={dragDropText ? "uppy-uploader uppy-uploader--with-text" : "uppy-uploader"}
        css={Css.df.fdc.aic.jcc.$}
      >
        <DragDrop
          key={dragDropText} // force re-render to show current dragDropText
          uppy={uppy}
          height={dragDropHeight}
          width={isUploading ? "100%" : dragDropWidth}
          locale={{ strings: { dropHereOr: dragDropText } }}
        />
        <StatusBar uppy={uppy} hideUploadButton hideCancelButton hidePauseResumeButton />
      </div>
    </div>
  );
}
